import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, FolderTree, Image as ImageIcon, Loader2, Search, X } from "lucide-react";
import { updateDealCategories } from "../../requests/Uploaded_Deals/UploadedDealsService";
import {
  fetchCategories,
  fetchSubcategories,
  isSubcategoryRecord,
} from "../../requests/Categories/CategoryService";

const STEPS = [
  { id: "category", label: "Category" },
  { id: "subcategory", label: "Subcategory" },
  { id: "review", label: "Review" },
];

let categorySelectionDraft = null;

const dealSelectionKey = (deal) => String(deal?.id ?? deal?.deal_slug ?? deal?.slug ?? "");

const itemName = (item) => item?.name || item?.categoryName || "Untitled";

const firstImage = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const itemThumbnail = (item) =>
  firstImage(
    item?.thumbnail,
    item?.thumbnail_url,
    item?.thumb,
    item?.logo,
    item?.image,
    item?.image_url
  );

const isInactive = (item) => {
  const raw = item?.status ?? item?.is_active ?? item?.isActive;
  if (raw === false || raw === 0) return true;
  const status = String(raw ?? "active").toLowerCase();
  return status === "inactive" || status === "hidden" || status === "0" || status === "false";
};

const sameText = (left, right) =>
  String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase();

const asLabel = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(asLabel).filter(Boolean).join(", ");
  return value.name || value.categoryName || value.title || "";
};

const asId = (value) => {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) return asId(value[0]);
  if (typeof value === "object") return value.id != null ? String(value.id) : "";
  return String(value);
};

const firstLabel = (value) => {
  if (Array.isArray(value)) return firstLabel(value[0]);
  return asLabel(value);
};

const readCurrentAssignment = (deal) => ({
  categoryId: asId(deal?.category_id ?? deal?.categoryId ?? (typeof deal?.category === "object" ? deal.category : "")),
  categoryName: firstLabel(deal?.category_name ?? deal?.categoryName ?? deal?.category),
  categoryDisplay: asLabel(deal?.category_name ?? deal?.categoryName ?? deal?.category),
  subcategoryId: asId(
    deal?.subcategory_id ??
      deal?.sub_category_id ??
      deal?.subcategoryId ??
      (typeof deal?.subcategory === "object" ? deal.subcategory : "") ??
      (typeof deal?.sub_category === "object" ? deal.sub_category : "")
  ),
  subcategoryName: firstLabel(
    deal?.subcategory_name ?? deal?.sub_category_name ?? deal?.subcategory ?? deal?.sub_category
  ),
  subcategoryDisplay: asLabel(
    deal?.subcategory_name ?? deal?.sub_category_name ?? deal?.subcategory ?? deal?.sub_category
  ),
});

function Stepper({ step }) {
  return (
    <ol className="grid grid-cols-3 gap-2" aria-label="Category change steps">
      {STEPS.map((item, index) => {
        const complete = index < step;
        const current = index === step;
        return (
          <li key={item.id} className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                  current
                    ? "bg-[#FF6C6C] text-white"
                    : complete
                      ? "bg-[#FFF1F0] text-[#CC2E2E]"
                      : "bg-gray-100 text-gray-400"
                }`}
                aria-hidden="true"
              >
                {complete ? <Check size={12} strokeWidth={2.5} /> : index + 1}
              </span>
              <span
                className={`truncate text-xs font-semibold ${
                  current ? "text-gray-900" : complete ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {item.label}
              </span>
            </div>
            <span
              className={`mt-2 block h-1 rounded-full ${
                current || complete ? "bg-[#FF6C6C]" : "bg-gray-100"
              }`}
              aria-hidden="true"
            />
          </li>
        );
      })}
    </ol>
  );
}

function ChoiceRow({ selected, name, thumbnail, meta, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
        selected
          ? "border-[#FF6C6C] bg-[#FFF1F0]"
          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
        {thumbnail ? (
          <img src={thumbnail} alt="" className="h-full w-full object-cover" />
        ) : (
          <ImageIcon size={14} className="text-slate-300" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-gray-900">{name}</span>
        {meta ? <span className="block truncate text-[11px] text-gray-500">{meta}</span> : null}
      </span>
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          selected ? "border-[#FF6C6C] bg-[#FF6C6C]" : "border-gray-300 bg-white"
        }`}
        aria-hidden="true"
      >
        {selected ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
      </span>
    </button>
  );
}

function ListState({ loading, error, empty, onRetry, children }) {
  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-live="polite">
        <p className="sr-only">Loading</p>
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="h-[58px] animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-4 text-center">
        <p className="text-sm font-medium text-rose-700">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
        >
          Try again
        </button>
      </div>
    );
  }

  if (empty) {
    return empty;
  }

  return children;
}

const ChangeDealCategoryDrawer = ({ isOpen, onClose, onSaved, deal }) => {
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [categoryReload, setCategoryReload] = useState(0);
  const [subcategories, setSubcategories] = useState([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [subcategoriesError, setSubcategoriesError] = useState("");
  const [subcategoryReload, setSubcategoryReload] = useState(0);
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [categoryQuery, setCategoryQuery] = useState("");
  const [subcategoryQuery, setSubcategoryQuery] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const errorRef = useRef(null);
  const headingRef = useRef(null);
  const pendingSubRef = useRef("");
  const didPrefill = useRef(false);
  const savedRef = useRef(false);
  const requestId = useRef(0);
  const selectionRef = useRef({ step: 0, categoryId: "", subcategoryId: "" });
  selectionRef.current = { step, categoryId, subcategoryId };

  const current = useMemo(() => readCurrentAssignment(deal), [deal]);
  const parents = useMemo(
    () => (categories || []).filter((item) => item?.id && !isSubcategoryRecord(item)),
    [categories]
  );
  const selectedCategory = parents.find((item) => String(item.id) === String(categoryId)) || null;
  const selectedSubcategory =
    subcategories.find((item) => String(item.id) === String(subcategoryId)) || null;

  const visibleCategories = parents.filter((item) =>
    itemName(item).toLowerCase().includes(categoryQuery.trim().toLowerCase())
  );
  const visibleSubcategories = subcategories.filter((item) =>
    itemName(item).toLowerCase().includes(subcategoryQuery.trim().toLowerCase())
  );

  const categoryUnchanged = current.categoryId
    ? String(categoryId) === String(current.categoryId)
    : Boolean(current.categoryName) && sameText(selectedCategory ? itemName(selectedCategory) : "", current.categoryName);
  const subcategoryUnchanged = current.subcategoryId
    ? String(subcategoryId || "") === String(current.subcategoryId)
    : current.subcategoryName
      ? sameText(selectedSubcategory ? itemName(selectedSubcategory) : "", current.subcategoryName)
      : !subcategoryId;
  const unchanged =
    Boolean(categoryId) &&
    categoryUnchanged &&
    subcategoryUnchanged &&
    Boolean(current.categoryId || current.categoryName);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose, saving]);

  useEffect(() => {
    const key = dealSelectionKey(deal);
    if (!isOpen) {
      const latest = selectionRef.current;
      if (savedRef.current) {
        savedRef.current = false;
        if (categorySelectionDraft?.dealKey === key) categorySelectionDraft = null;
      } else if (key && latest.categoryId) {
        categorySelectionDraft = {
          dealKey: key,
          step: latest.step,
          categoryId: latest.categoryId,
          subcategoryId: latest.subcategoryId,
        };
      }
      didPrefill.current = false;
      return;
    }

    if (categorySelectionDraft?.dealKey === key && categorySelectionDraft.categoryId) {
      const draft = categorySelectionDraft;
      pendingSubRef.current = draft.subcategoryId || "";
      didPrefill.current = true;
      setStep(draft.step || 0);
      setCategoryId(draft.categoryId);
      setSubcategoryId(draft.subcategoryId || "");
      setCategoryQuery("");
      setSubcategoryQuery("");
      setErrors({});
      setSaving(false);
      return;
    }

    categorySelectionDraft = null;
    didPrefill.current = false;
    pendingSubRef.current = "";
    setStep(0);
    setCategoryId("");
    setSubcategoryId("");
    setCategoryQuery("");
    setSubcategoryQuery("");
    setSubcategories([]);
    setSubcategoriesError("");
    setErrors({});
    setSaving(false);
  }, [isOpen, deal?.id]);

  useEffect(() => {
    if (!isOpen) return;
    fetchCategories(setCategories, setCategoriesLoading, setCategoriesError);
  }, [isOpen, categoryReload]);

  useEffect(() => {
    if (!isOpen || didPrefill.current || categoriesLoading || parents.length === 0) return;
    didPrefill.current = true;
    const match = parents.find((item) => {
      if (current.categoryId && String(item.id) === String(current.categoryId)) return true;
      return current.categoryName && sameText(itemName(item), current.categoryName);
    });
    if (!match) return;
    pendingSubRef.current = current.subcategoryId || current.subcategoryName || "";
    setCategoryId(String(match.id));
  }, [isOpen, categoriesLoading, parents, current]);

  useEffect(() => {
    if (!isOpen || !categoryId) {
      setSubcategories([]);
      setSubcategoriesLoading(false);
      setSubcategoriesError("");
      return undefined;
    }

    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    let active = true;
    setSubcategoriesLoading(true);
    setSubcategoriesError("");
    setSubcategoryId("");

    fetchSubcategories(categoryId)
      .then((list) => {
        if (!active || requestId.current !== currentRequest) return;
        const next = Array.isArray(list) ? list : [];
        setSubcategories(next);
        const pending = pendingSubRef.current;
        if (pending) {
          const match = next.find(
            (item) => String(item.id) === String(pending) || sameText(itemName(item), pending)
          );
          if (match) setSubcategoryId(String(match.id));
          pendingSubRef.current = "";
        }
      })
      .catch((error) => {
        if (!active || requestId.current !== currentRequest) return;
        setSubcategories([]);
        setSubcategoriesError(error?.message || "Failed to fetch subcategories");
      })
      .finally(() => {
        if (active && requestId.current === currentRequest) setSubcategoriesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, categoryId, subcategoryReload]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => headingRef.current?.focus(), 40);
    return () => clearTimeout(timer);
  }, [isOpen, step]);

  const validateStep = (index) => {
    if (index === 0 && !categoryId) {
      return { category: "Choose a category." };
    }
    if (index === 1) {
      if (subcategoriesLoading) return { subcategory: "Wait for subcategories to finish loading." };
      if (subcategoriesError) return { subcategory: "Subcategories could not be loaded." };
      if (subcategories.length > 0 && !subcategoryId) {
        return { subcategory: "Choose a subcategory." };
      }
    }
    if (index === 2 && unchanged) {
      return { review: "This deal is already in this category and subcategory." };
    }
    return null;
  };

  const goNext = () => {
    const problem = validateStep(step);
    if (problem) {
      setErrors(problem);
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    setErrors({});
    setStep((value) => Math.min(value + 1, STEPS.length - 1));
  };

  const goBack = () => {
    if (saving) return;
    setErrors({});
    setStep((value) => Math.max(value - 1, 0));
  };

  const save = async () => {
    const problem = validateStep(0) || validateStep(1) || validateStep(2);
    if (problem) {
      setErrors(problem);
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    const dealSlug = deal?.deal_slug || deal?.slug;
    if (!dealSlug) {
      setErrors({ review: "This deal has no slug." });
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    setSaving(true);
    setErrors({});
    await updateDealCategories(
      dealSlug,
      {
        categoryId,
        subcategoryId: subcategoryId || null,
      },
      {
        onSuccess: () => {
          savedRef.current = true;
          categorySelectionDraft = null;
          if (typeof onSaved === "function") onSaved();
          onClose();
        },
        onError: (error) => {
          setErrors({ review: error?.message || "Failed to update category" });
          requestAnimationFrame(() => errorRef.current?.focus());
        },
      }
    );
    setSaving(false);
  };

  const errorEntries = Object.entries(errors).filter(([, message]) => message);
  const dealTitle = deal?.title || deal?.name || "This deal";
  const continueLabel =
    step === 1 && !subcategoriesLoading && !subcategoriesError && subcategories.length === 0
      ? "Continue without subcategory"
      : "Continue";

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="change-category-backdrop"
          className="fixed inset-0 z-[1000] bg-black/45"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            if (!saving) onClose();
          }}
        />
      )}
      {isOpen && (
        <motion.div
          key="change-category-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-category-title"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="fixed inset-y-0 right-0 z-[1001] flex h-full w-full max-w-[440px] flex-col overflow-hidden bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3 pt-4">
            <div className="flex min-w-0 items-start gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FFF1F0] text-[#FF6C6C]">
                <FolderTree size={16} />
              </span>
              <div className="min-w-0">
                <h2 id="change-category-title" className="text-sm font-semibold text-gray-900">
                  Change category
                </h2>
                <p className="truncate text-xs text-gray-500">{dealTitle}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!saving) onClose();
              }}
              disabled={saving}
              aria-label="Close"
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X size={16} />
            </button>
          </div>

          <div className="shrink-0 px-4 pb-3">
            <Stepper step={step} />
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 pb-4">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                Current assignment
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {current.categoryDisplay || "No category"}
              </p>
              <p className="text-xs text-gray-500">
                {current.subcategoryDisplay || "No subcategory"}
                {deal?.company_name ? ` · ${deal.company_name}` : ""}
              </p>
            </div>

            {errorEntries.length > 0 && (
              <div
                ref={errorRef}
                tabIndex={-1}
                role="alert"
                aria-labelledby="change-category-error-title"
                className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 outline-none"
              >
                <p id="change-category-error-title" className="text-sm font-semibold text-rose-800">
                  There is a problem
                </p>
                <ul className="mt-1 space-y-1">
                  {errorEntries.map(([field, message]) => (
                    <li key={field}>
                      <button
                        type="button"
                        className="text-left text-xs font-medium text-rose-700 underline"
                        onClick={() => document.getElementById(`change-${field}-field`)?.focus()}
                      >
                        {message}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <h3 ref={headingRef} tabIndex={-1} className="sr-only">
              {STEPS[step].label}
            </h3>

            {step === 0 && (
              <section>
                <label htmlFor="change-category-search" className="mb-1 block text-xs font-semibold text-gray-800">
                  Category
                </label>
                <p id="change-category-help" className="mb-2 text-[11px] text-gray-400">
                  Choose the department this deal should sit under.
                </p>
                <div className="relative mb-2">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    id="change-category-search"
                    value={categoryQuery}
                    onChange={(event) => setCategoryQuery(event.target.value)}
                    placeholder="Search category name"
                    aria-describedby="change-category-help"
                    className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-gray-300"
                  />
                </div>
                <div
                  id="change-category-field"
                  tabIndex={-1}
                  role="radiogroup"
                  aria-label="Categories"
                  aria-invalid={errors.category ? "true" : "false"}
                  aria-describedby={errors.category ? "change-category-error" : undefined}
                  className="space-y-2 outline-none"
                >
                  <ListState
                    loading={categoriesLoading}
                    error={categoriesError}
                    onRetry={() => setCategoryReload((value) => value + 1)}
                    empty={
                      !categoriesLoading && visibleCategories.length === 0 ? (
                        <p className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                          {categoryQuery ? "No categories match that name." : "No categories yet."}
                        </p>
                      ) : null
                    }
                  >
                    {visibleCategories.map((item) => (
                      <ChoiceRow
                        key={item.id}
                        selected={String(item.id) === String(categoryId)}
                        name={itemName(item)}
                        thumbnail={itemThumbnail(item)}
                        meta={isInactive(item) ? "Hidden from shoppers" : "Parent category"}
                        onSelect={() => {
                          if (String(item.id) === String(categoryId)) return;
                          pendingSubRef.current = "";
                          setCategoryId(String(item.id));
                          setSubcategoryId("");
                          setErrors((value) => ({ ...value, category: "" }));
                        }}
                      />
                    ))}
                  </ListState>
                </div>
                <p id="change-category-error" className="mt-2 min-h-[1rem] text-xs text-rose-600" role="status">
                  {errors.category || ""}
                </p>
              </section>
            )}

            {step === 1 && (
              <section>
                <label htmlFor="change-subcategory-search" className="mb-1 block text-xs font-semibold text-gray-800">
                  Subcategory
                </label>
                <p id="change-subcategory-help" className="mb-2 text-[11px] text-gray-400">
                  {selectedCategory
                    ? `Under ${itemName(selectedCategory)}. A subcategory is required when this category has one.`
                    : "Choose a category first."}
                </p>
                <div className="relative mb-2">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    id="change-subcategory-search"
                    value={subcategoryQuery}
                    onChange={(event) => setSubcategoryQuery(event.target.value)}
                    placeholder="Search subcategory name"
                    disabled={subcategoriesLoading || Boolean(subcategoriesError)}
                    aria-describedby="change-subcategory-help"
                    className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
                <div
                  id="change-subcategory-field"
                  tabIndex={-1}
                  role="radiogroup"
                  aria-label="Subcategories"
                  aria-invalid={errors.subcategory ? "true" : "false"}
                  aria-describedby={errors.subcategory ? "change-subcategory-error" : undefined}
                  className="space-y-2 outline-none"
                >
                  <ListState
                    loading={subcategoriesLoading}
                    error={subcategoriesError}
                    onRetry={() => setSubcategoryReload((value) => value + 1)}
                    empty={
                      !subcategoriesLoading && !subcategoriesError && subcategories.length === 0 ? (
                        <p className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                          This category has no subcategories. You can continue with the category only.
                        </p>
                      ) : !subcategoriesLoading && visibleSubcategories.length === 0 ? (
                        <p className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                          No subcategories match that name.
                        </p>
                      ) : null
                    }
                  >
                    {visibleSubcategories.map((item) => (
                      <ChoiceRow
                        key={item.id}
                        selected={String(item.id) === String(subcategoryId)}
                        name={itemName(item)}
                        thumbnail={itemThumbnail(item)}
                        meta={isInactive(item) ? "Hidden from shoppers" : itemName(selectedCategory)}
                        onSelect={() => {
                          setSubcategoryId(String(item.id));
                          setErrors((value) => ({ ...value, subcategory: "" }));
                        }}
                      />
                    ))}
                  </ListState>
                </div>
                <p id="change-subcategory-error" className="mt-2 min-h-[1rem] text-xs text-rose-600" role="status">
                  {errors.subcategory || ""}
                </p>
              </section>
            )}

            {step === 2 && (
              <section id="change-review-field" tabIndex={-1} className="space-y-3 outline-none">
                <p className="text-xs font-semibold text-gray-800">Review the change</p>
                <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
                  <div className="min-w-0 rounded-2xl border border-gray-200 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">From</p>
                    <p className="mt-1 break-words text-sm font-semibold text-gray-900">
                      {current.categoryDisplay || "No category"}
                    </p>
                    <p className="break-words text-xs text-gray-500">{current.subcategoryDisplay || "No subcategory"}</p>
                  </div>
                  <div className="flex items-center text-gray-300" aria-hidden="true">
                    <ArrowRight size={16} />
                  </div>
                  <div className="min-w-0 rounded-2xl border border-[#FF6C6C]/30 bg-[#FFF1F0] p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#CC2E2E]">To</p>
                    <p className="mt-1 break-words text-sm font-semibold text-gray-900">
                      {selectedCategory ? itemName(selectedCategory) : "No category"}
                    </p>
                    <p className="break-words text-xs text-gray-600">
                      {selectedSubcategory
                        ? itemName(selectedSubcategory)
                        : "No subcategory"}
                    </p>
                  </div>
                </div>
                <p
                  id="change-review-error"
                  className={`min-h-[1rem] text-xs ${errors.review ? "text-rose-600" : "text-gray-500"}`}
                  role="status"
                >
                  {errors.review ||
                    (unchanged ? "This deal is already in this category and subcategory." : "")}
                </p>
                {saving && (
                  <p role="status" className="text-xs font-medium text-gray-500">
                    Saving category…
                  </p>
                )}
              </section>
            )}
          </div>

          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
            <button
              type="button"
              onClick={step === 0 ? () => !saving && onClose() : goBack}
              disabled={saving}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {step === 0 ? "Cancel" : "Back"}
            </button>
            <button
              type="button"
              onClick={step === 2 ? save : goNext}
              disabled={saving || (step === 0 ? categoriesLoading : step === 1 ? subcategoriesLoading : false)}
              className="inline-flex items-center gap-2 rounded-xl bg-[#FF6C6C] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ff5a5a] disabled:cursor-not-allowed disabled:bg-[#FF6C6C]/50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {step === 2 ? (saving ? "Saving" : "Save category") : continueLabel}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ChangeDealCategoryDrawer;
